"use client";

import { useEffect, useState } from "react";
import { useSearch } from "@/context/SearchContext";
import Fuse from "fuse.js";
import ToolCard from "./ToolCard";
import { useInView } from "react-intersection-observer";
import Image from "next/image";
import { X, Plus } from "lucide-react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

export default function ToolsCards({ tools }) {
  const { query } = useSearch();
  const { ref, inView } = useInView();

  const [filtered, setFiltered] = useState(tools);
  const [visibleCount, setVisibleCount] = useState(6);

  const [activeLabel, setActiveLabel] = useState(null);
  const [activePrice, setActivePrice] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => {
    let result = tools;

    if (query) {
      const fuse = new Fuse(tools, {
        keys: ["title", "description", "categories"],
        threshold: 0.3,
      });
      result = fuse.search(query).map((r) => r.item);
    }

    if (activeCategory) {
      result = result.filter((tool) =>
        tool.categories?.some(
          (cat) => cat.toLowerCase() === activeCategory.toLowerCase()
        )
      );
    }

    if (activePrice)
      result = result.filter((tool) => tool.price === activePrice);

    if (activeLabel)
      result = result.filter((tool) => tool.label === activeLabel);

    setFiltered(result);
  }, [query, tools, activeCategory, activePrice, activeLabel]);

  useEffect(() => {
    if (inView && visibleCount < filtered.length) {
      setVisibleCount((prev) => prev + 6);
    }
  }, [inView, filtered.length]);

  const categories = Array.from(
    new Set(
      tools.flatMap((t) => (t.categories || []).map((c) => c.toLowerCase()))
    )
  );
  const prices = Array.from(new Set(tools.map((t) => t.price).filter(Boolean)));
  const labels = Array.from(new Set(tools.map((t) => t.label).filter(Boolean)));

  const formatCategory = (cat) =>
    cat
      .split("-")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join("-");

  if (filtered.length === 0) {
    return (
      <div className="w-full text-center pt-7 text-lg color_red">
        {(activeCategory || activePrice || activeLabel) && (
          <div className="flex flex-wrap gap-2 justify-center neue_machina_regular capitalize">
            {activeLabel && (
              <div className="flex items-center gap-1 text-black px-3 pb-1 pt-1 border border-dashed border-black text-xs">
                <span>
                  {activeLabel.toLowerCase() === "most_popular"
                    ? "Most Popular"
                    : activeLabel}
                </span>
                <button onClick={() => setActiveLabel(null)}>
                  <X size={12} className="color_black" />
                </button>
              </div>
            )}
            {activeCategory && (
              <div className="flex items-center gap-1 text-black px-3 pb-1 pt-1 border border-dashed border-black text-xs">
                <span>{activeCategory}</span>
                <button onClick={() => setActiveCategory(null)}>
                  <X size={12} className="color_black" />
                </button>
              </div>
            )}
            {activePrice && (
              <div className="flex items-center gap-1 text-black px-3 pb-1 pt-1 border border-dashed border-black text-xs">
                <span>{activePrice}</span>
                <button onClick={() => setActivePrice(null)}>
                  <X size={12} className="color_black" />
                </button>
              </div>
            )}
            {(activeCategory || activePrice || activeLabel) && (
              <button
                onClick={() => {
                  setActiveCategory(null);
                  setActivePrice(null);
                  setActiveLabel(null);
                }}
                className="hover:bg-red-600 hover:text-white text-black px-3 pb-1 pt-1 border border-dashed border-red-500 text-xs transition-colors"
              >
                Reset All ✨
              </button>
            )}
          </div>
        )}

        <div className="h-[1px] bg-black my-5" />

        <p className="shadow_hand text-2xl color_black">
          🤔 No tools found! 🤷
        </p>
        <p>
          Either it doesn’t exist... or you typed like a cat walking on a
          keyboard 🐈🐾😁😎
        </p>
        <div className="flex justify-center items-center mt-6">
          <Image
            src="/images/empty.gif"
            alt="empty"
            width={350}
            height={350}
            className="border border-black"
          />
        </div>
      </div>
    );
  }

  return (
    <section className="lg:px-6 pt-10">
      <div className="flex items-center gap-y-6 sm:justify-between justify-center flex-wrap sm:flex-nowrap">
        <div className="flex flex-wrap justify-center gap-4 neue_machina_regular neue_machina_regular select-none">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="border-2 border-dashed border-black rounded-none color_black cursor-pointer capitalize"
              >
                Labels{" "}
                <div className="focus:rotate-45 hover:rotate-45">
                  <Plus />
                </div>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-fit cursor-pointer capitalize">
              <div className="flex flex-col gap-2">
                {labels.map((label, i) => (
                  <label
                    key={i}
                    className="flex items-center gap-2 select-none"
                  >
                    <Checkbox
                      className="border-black rounded-none border-dashed"
                      checked={activeLabel === label}
                      onCheckedChange={() =>
                        setActiveLabel((prev) =>
                          prev === label ? null : label
                        )
                      }
                    />
                    <span className="text-sm neue_machina_regular">
                      {label.toLowerCase() === "most_popular"
                        ? "Most Popular"
                        : label}
                    </span>
                  </label>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="border-2 border-dashed border-black rounded-none color_black cursor-pointer capitalize"
              >
                Prices{" "}
                <div className="focus:rotate-45 hover:rotate-45">
                  <Plus />
                </div>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-fit cursor-pointer capitalize">
              <div className="flex flex-col gap-2">
                {prices.map((price, i) => (
                  <label
                    key={i}
                    className="flex items-center gap-2 select-none"
                  >
                    <Checkbox
                      className="border-black rounded-none border-dashed"
                      checked={activePrice === price}
                      onCheckedChange={() =>
                        setActivePrice((prev) =>
                          prev === price ? null : price
                        )
                      }
                    />
                    <span className="text-sm neue_machina_regular">
                      {price}
                    </span>
                  </label>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="border-2 border-dashed border-black rounded-none color_black cursor-pointer capitalize"
              >
                Categories{" "}
                <div className="focus:rotate-45 hover:rotate-45">
                  <Plus />
                </div>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-fit cursor-pointer capitalize">
              <div className="flex flex-col gap-4 pr-6 max-h-60 scroll_thin overflow-y-auto">
                {categories.map((cat, idx) => (
                  <label
                    key={idx}
                    className="flex items-center gap-3 select-none"
                  >
                    <Checkbox
                      className="border-black rounded-none border-dashed"
                      checked={activeCategory === cat}
                      onCheckedChange={() =>
                        setActiveCategory((prev) => (prev === cat ? null : cat))
                      }
                    />
                    <span className="text-sm neue_machina_regular">
                      {formatCategory(cat)}
                    </span>
                  </label>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>
        {(activeCategory || activePrice || activeLabel) && (
          <div className="flex flex-wrap gap-2 justify-center neue_machina_regular capitalize select-none">
            {activeLabel && (
              <div className="flex items-center gap-1 text-black px-3 pb-1 pt-1 border border-dashed border-black text-xs">
                <span>
                  {activeLabel.toLowerCase() === "most_popular"
                    ? "Most Popular"
                    : activeLabel}
                </span>
                <button onClick={() => setActiveLabel(null)}>
                  <X size={12} className="color_black" />
                </button>
              </div>
            )}
            {activePrice && (
              <div className="flex items-center gap-1 text-black px-3 pb-1 pt-1 border border-dashed border-black text-xs">
                <span>{activePrice}</span>
                <button onClick={() => setActivePrice(null)}>
                  <X size={12} className="color_black" />
                </button>
              </div>
            )}
            {activeCategory && (
              <div className="flex items-center gap-1 text-black px-3 pb-1 pt-1 border border-dashed border-black text-xs">
                <span>{activeCategory}</span>
                <button onClick={() => setActiveCategory(null)}>
                  <X size={12} className="color_black" />
                </button>
              </div>
            )}
            {(activeCategory || activePrice || activeLabel) && (
              <button
                onClick={() => {
                  setActiveCategory(null);
                  setActivePrice(null);
                  setActiveLabel(null);
                }}
                className="hover:bg-red-600 hover:text-white text-black px-3 pb-1 pt-1 border border-dashed border-red-500 text-xs transition-colors"
              >
                Reset All ✨
              </button>
            )}
          </div>
        )}
      </div>

      <div className="w-full h-[1px] bg-black my-5" />

      <div className="shadow_hand text-3xl color_red pt-5">
        All Tools - {filtered.length}
      </div>

      <section className="grid grid-cols-12 gap-4 pt-10">
        {filtered.slice(0, visibleCount).map((tool) => (
          <div
            key={tool._id}
            className="col-span-12 sm:col-span-6 md:col-span-4"
          >
            <ToolCard tool={tool} />
          </div>
        ))}
      </section>

      <div ref={ref} className="text-center py-6">
        {visibleCount < filtered.length && (
          <span className="text-sm text-gray-500">
            ⏳ Loading more tools...
          </span>
        )}
      </div>
    </section>
  );
}
